import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { DriverPaymentResponse, DriverPaymentType } from '../../core/models/portal.models';

@Component({
  selector: 'app-payment-page',
  standalone: true,
  imports: [RouterLink, DecimalPipe, DatePipe],
  templateUrl: './payment-page.component.html'
})
export class PaymentPageComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private confirmationTimer: ReturnType<typeof setTimeout> | undefined;
  private returnedFromStripe = false;
  private paymentReturnAuditRecorded = false;
  protected readonly payment = signal<DriverPaymentResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly submitting = signal(false);
  protected readonly confirming = signal(false);
  protected readonly selectedType = signal<DriverPaymentType>('ONE_TIME');
  protected readonly cancelling = signal(false);
  protected readonly error = signal('');
  protected readonly cancelled = signal(false);

  ngOnInit(): void {
    this.cancelled.set(this.route.snapshot.queryParamMap.get('cancelled') === '1');
    this.returnedFromStripe = this.route.snapshot.queryParamMap.get('success') === '1';
    this.loadStatus(this.returnedFromStripe ? 0 : undefined);
  }

  ngOnDestroy(): void {
    if (this.confirmationTimer) {
      clearTimeout(this.confirmationTimer);
    }
  }

  protected loadStatus(retryAttempt?: number): void {
    this.loading.set(true);
    this.error.set('');
    this.api.driverPaymentStatus().subscribe({
      next: (payment) => {
        this.payment.set(payment);
        this.loading.set(false);

        if (this.returnedFromStripe && payment.paid) {
          this.confirming.set(false);
          this.recordPaymentReturnAndContinue();
          return;
        }

        const shouldRetry = this.returnedFromStripe
          && !payment.paid
          && retryAttempt !== undefined
          && retryAttempt < 10;
        this.confirming.set(shouldRetry);
        if (shouldRetry) {
          this.confirmationTimer = setTimeout(() => this.loadStatus(retryAttempt + 1), 1500);
        }
      },
      error: (response) => {
        const message = response?.error?.message;
        if (message) {
          this.error.set(message);
        } else {
        this.error.set('Der Zahlungsstatus konnte nicht geladen werden. Bitte versuche es erneut.');
        this.loading.set(false);
        this.confirming.set(false);
      }
      }
    });
  }

  private recordPaymentReturnAndContinue(): void {
    if (this.paymentReturnAuditRecorded) {
      return;
    }
    this.paymentReturnAuditRecorded = true;
    this.api.recordDriverPaymentReturn().subscribe({
      next: () => void this.router.navigateByUrl('/learning'),
      error: () => void this.router.navigateByUrl('/learning')
    });
  }

  protected startPayment(): void {
    this.submitting.set(true);
    this.error.set('');
    this.api.createDriverCheckoutSession(this.selectedType()).subscribe({
      next: ({ checkoutUrl }) => window.location.assign(checkoutUrl),
      error: (response) => {
        const message = response?.error?.message;
        if (message) {
          this.error.set(message);
        } else {
        this.error.set('Die Zahlung konnte nicht gestartet werden. Bitte versuche es später erneut.');
        this.submitting.set(false);
      }
      }
    });
  }

  protected openBillingPortal(): void {
    this.cancelling.set(true);
    this.error.set('');
    this.api.createDriverPortalSession().subscribe({
      next: ({ portalUrl }) => window.location.assign(portalUrl),
      error: () => {
        this.error.set('Das Stripe-Kundenportal konnte nicht geöffnet werden.');
        this.cancelling.set(false);
      }
    });
  }
}
